import React, { useState, useEffect } from 'react';
import './ChartGuide.css';
import PixelIcon from './PixelIcon';
import { TIMELINE_EVENTS, AI2027_URL } from '../utils/timelineConfig';
import { isBrake, racePressure, riskLevel } from '../utils/dimensions';

const DAY_MS = 24 * 60 * 60 * 1000;

function timing(date, now) {
  const days = Math.round((date - now) / DAY_MS);
  if (days < -30) return `Passed ${Math.round(-days / 30)} months ago`;
  if (days < 0) return `Passed ${-days} days ago`;
  if (days === 0) return 'Today';
  if (days < 60) return `In ${days} days`;
  return `In about ${Math.round(days / 30)} months`;
}

function MilestoneDetail({ event, dimensions, onClose }) {
  const now = new Date();
  return (
    <div className={`milestone-detail ${event.bifurcation ? 'branch' : ''}`} role="region" aria-label={`${event.id}: ${event.titleShort}`}>
      <div className="detail-header">
        <div>
          <div className="detail-kicker">
            {event.id} · {event.period} · {timing(event.date, now)}
          </div>
          <h3 className="detail-title">{event.titleShort}</h3>
        </div>
        <button className="detail-close" onClick={onClose} aria-label="Close details">
          <PixelIcon name="close" size={12} />
        </button>
      </div>

      <p className="detail-summary">{event.summary}</p>

      <div className="detail-watch">
        <div className="detail-label">WHAT TO WATCH IN THE MONITOR</div>
        <p>{event.watch}</p>
        {dimensions && (
          <ul className="detail-dims">
            {event.dimensions.filter(key => dimensions[key]).map(key => {
              const dim = dimensions[key];
              const risk = riskLevel(racePressure(key, dim.value));
              return (
                <li key={key}>
                  <span className="detail-dim-name">{dim.name}</span>
                  <span className="detail-dim-kind">{isBrake(key) ? 'brake' : 'accelerator'}</span>
                  <span className={`detail-dim-value ${risk.className}`}>{dim.value.toFixed(2)}</span>
                  <span className={`detail-dim-risk ${risk.className}`}>{risk.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <a className="detail-source" href={AI2027_URL} target="_blank" rel="noopener noreferrer">
        Read "{event.period}: {event.titleShort}" on ai-2027.com <PixelIcon name="link" size={10} />
      </a>
    </div>
  );
}

// Full-width companion to the Temporal Evolution chart: milestones legend + reading guide
function ChartGuide({ dimensions }) {
  const [selectedId, setSelectedId] = useState(null);
  const now = new Date();
  const selected = TIMELINE_EVENTS.find(e => e.id === selectedId);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  return (
    <section className="chart-guide" aria-label="Chart guide">
      <div className="milestones">
        <div className="milestones-title">
          AI 2027 MILESTONES <span className="milestones-hint">click one to read more</span>
        </div>
        <ol className="milestones-list">
          {TIMELINE_EVENTS.map(event => (
            <li key={event.id}>
              <button
                type="button"
                className={`milestone ${event.date <= now ? 'past' : 'upcoming'} ${event.bifurcation ? 'branch' : ''} ${event.id === selectedId ? 'selected' : ''}`}
                onClick={() => setSelectedId(event.id === selectedId ? null : event.id)}
                aria-expanded={event.id === selectedId}
              >
                <span className="milestone-id">{event.id}</span>
                <span className="milestone-period">{event.period}</span>
                <span className="milestone-title">{event.titleShort}</span>
              </button>
            </li>
          ))}
        </ol>

        {selected && (
          <MilestoneDetail event={selected} dimensions={dimensions} onClose={() => setSelectedId(null)} />
        )}
      </div>

      <div className="chart-howto">
        <div className="howto-title">HOW TO READ THE CHART</div>
        <ul>
          <li><span className="text-red">Red line, Race Index:</span> how strong the accelerators are (military, centralization, economic dependency). Higher is worse.</li>
          <li><span className="text-green">Green line, Slowdown Index:</span> how strong the brakes are (governance, compute control, cooperation, critical public debate). Higher is better.</li>
          <li><span className="text-yellow">Caution and danger lines</span> apply to the red line only. The green line above 1.5 is good news, not a warning.</li>
          <li>What matters most is the <strong>gap</strong>: red well above green means the race is winning; when the lines meet, the scenario is balanced.</li>
          <li><span className="text-yellow">Striped area, low data:</span> before February 2026 automatic analysis was not running yet, and the archive holds only about 20 older articles a month. The jump in February is the system switching on, not an event in the world.</li>
          <li>Vertical lines are the milestones on the left (solid = passed, dashed = upcoming). There are no projections after NOW: the chart only shows what has been measured.</li>
        </ul>
      </div>
    </section>
  );
}

export default ChartGuide;
